import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GET, POST, PUT, DELETE } from '../../services/api';

// Add Advertisement
export const addAdvertisement = createAsyncThunk(
  'advertisement/add',
  async (data, { rejectWithValue }) => {
    try {
      const response = await POST('/admin/advertisement', data);
      if (response?.status === 200) {
        return response.data.result;
      } else {
        return rejectWithValue(response?.data || 'Something went wrong');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'Failed to add advertisement');
    }
  }
);

// List Advertisements
export const listAdvertisements = createAsyncThunk(
  'advertisement/list',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await GET('/admin/advertisement',  { ...params });
      if (response?.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response?.data || 'Something went wrong');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'Failed to fetch advertisements');
    }
  }
);

// View Advertisement
export const viewAdvertisement = createAsyncThunk(
  'advertisement/view',
  async (advertisementId, { rejectWithValue }) => {
    try {
      const response = await GET(`/admin/advertisement/view`, { advertisement_id: advertisementId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'An error occurred');
    }
  }
);

// Update Advertisement
export const updateAdvertisement = createAsyncThunk(
  'advertisement/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await PUT(`/admin/advertisement?advertisement_id=${id}`, data);
      if (response?.status === 200) {
        return response.data.result;
      } else {
        return rejectWithValue(response?.data || 'Something went wrong');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'Failed to update advertisement');
    }
  }
);

// Delete Advertisement
export const deleteAdvertisement = createAsyncThunk(
  'advertisement/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await DELETE(`/admin/advertisement`, { advertisement_id: id });
      if (response?.status === 200) {
        return response.data.result;
      } else {
        return rejectWithValue(response?.data || 'Something went wrong');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || 'Failed to delete advertisement');
    }
  }
);

const AdvertisementSlice = createSlice({
  name: 'advertisement',
  initialState: {
    addAd: [],
    addAdLoading: false,
    addAdError: null,
    listAds: [],
    listAdsCount: '', 
    listAdsLoading: false,
    listAdsError: null,
    viewAd: [],
    viewAdLoading: false,
    viewAdError: null,
    updateAd: [],
    updateAdLoading: false,
    updateAdError: null,
    deleteAd: [],
    deleteAdLoading: false,
    deleteAdError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(viewAdvertisement.pending, (state) => {
        state.viewAdLoading = true;
        state.viewAdError = null;
      })
      .addCase(viewAdvertisement.fulfilled, (state, action) => {
        state.viewAdLoading = false;
        state.viewAd = action.payload.result;
      })
      .addCase(viewAdvertisement.rejected, (state, action) => {
        state.viewAdLoading = false;
        state.viewAdError = action.error.message;
      })
      .addCase(addAdvertisement.pending, (state) => {
        state.addAdLoading = true;
        state.addAdError = null;
      })
      .addCase(addAdvertisement.fulfilled, (state, action) => {
        state.addAdLoading = false;
        state.addAd = action.payload;
      })
      .addCase(addAdvertisement.rejected, (state, action) => {
        state.addAdLoading = false;
        state.addAdError = action.error.message;
      })
      .addCase(listAdvertisements.pending, (state) => {
        state.listAdsLoading = true;
        state.listAdsError = null;
      })
      .addCase(listAdvertisements.fulfilled, (state, action) => {
        state.listAdsLoading = false;
        state.listAds = action?.payload?.result?.rows;
        state.listAdsCount = action?.payload?.result?.count
      })
      .addCase(listAdvertisements.rejected, (state, action) => {
        state.listAdsLoading = false;
        state.listAdsError = action.error.message;
      })
      .addCase(updateAdvertisement.pending, (state) => {
        state.updateAdLoading = true;
        state.updateAdError = null;
      })
      .addCase(updateAdvertisement.fulfilled, (state, action) => {
        state.updateAdLoading = false;
        state.updateAd = action.payload;
      })
      .addCase(updateAdvertisement.rejected, (state, action) => {
        state.updateAdLoading = false;
        state.updateAdError = action.error.message;
      })
      .addCase(deleteAdvertisement.pending, (state) => {
        state.deleteAdLoading = true;
        state.deleteAdError = null;
      })
      .addCase(deleteAdvertisement.fulfilled, (state, action) => {
        state.deleteAdLoading = false;
        state.deleteAd = action.payload;
      })
      .addCase(deleteAdvertisement.rejected, (state, action) => {
        state.deleteAdLoading = false;
        state.deleteAdError = action.error.message;
      });
  },
});

export default AdvertisementSlice.reducer;
